import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import {
  LearningTrack,
  CatalogFilters,
  AvailabilityFilter,
  ProgressFilter,
  RecentContentFilter,
  CatalogSort,
} from '../shared/models';
import { AcademyCatalogService } from '../services/academy-catalog.service';
import { AcademyCatalogStateService } from '../services/academy-catalog-state.service';

const TRACKS_PER_PAGE = 6;

@Component({
  selector: 'ut-academy-catalog',
  templateUrl: './academy-catalog.component.html',
  styleUrls: ['./academy-catalog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AcademyCatalogComponent implements OnInit {
  allTracks: LearningTrack[] = [];
  filteredTracks: LearningTrack[] = [];
  displayedTracks: LearningTrack[] = [];
  currentPage = 1;
  totalPages = 1;
  isLoading = false;
  isAdmin = false;

  filters: CatalogFilters = {
    availability: 'all',
    progress: 'all',
    recentContent: 'all',
    sort: 'default',
  };

  availabilityOptions: { [key: string]: string } = {
    all: 'All',
    available: 'Available',
  };

  progressOptions: { [key: string]: string } = {
    all: 'All',
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed',
  };

  recentContentOptions: { [key: string]: string } = {
    all: 'All',
    recently_added: 'Recently Added',
    recently_updated: 'Recently Updated',
  };

  sortOptions: { [key: string]: string } = {
    default: 'Default',
    name_asc: 'Name A-Z',
    name_desc: 'Name Z-A',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private academyCatalogService: AcademyCatalogService,
    private catalogStateService: AcademyCatalogStateService,
  ) {}

  ngOnInit(): void {
    this.restoreState();
    this.loadAllTracks();
  }

  private restoreState(): void {
    const saved = this.catalogStateService.get();
    if (saved) {
      this.filters = { ...saved.filters };
      this.currentPage = saved.currentPage;
    }
  }

  private persistState(): void {
    this.catalogStateService.save(this.currentPage, this.filters);
  }

  async loadAllTracks(): Promise<void> {
    this.isLoading = true;
    this.cdr.markForCheck();

    try {
      [this.allTracks, this.isAdmin] = await Promise.all([
        this.academyCatalogService.getAllLearningTracks(),
        this.academyCatalogService.isCurrentUserAdmin(),
      ]);
      this.applyFiltersAndSort();
    } catch (error) {
      console.error('Failed to load learning tracks:', error);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  onAvailabilityChange(value: string): void {
    this.filters.availability = value as AvailabilityFilter;
    this.onFilterChange();
  }

  onProgressChange(value: string): void {
    this.filters.progress = value as ProgressFilter;
    this.onFilterChange();
  }

  onRecentContentChange(value: string): void {
    this.filters.recentContent = value as RecentContentFilter;
    this.onFilterChange();
  }

  onSortChange(value: string): void {
    this.filters.sort = value as CatalogSort;
    this.onFilterChange();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updateDisplayedTracks();
    this.persistState();
    this.cdr.markForCheck();

    document.getElementById('main-content-container')?.scrollTo({ top: 0 });
  }

  private onFilterChange(): void {
    this.currentPage = 1;
    this.applyFiltersAndSort();
    this.persistState();
    this.cdr.markForCheck();
  }

  private applyFiltersAndSort(): void {
    let tracks = this.filterTracks(this.allTracks);
    tracks = this.sortTracks(tracks);
    this.filteredTracks = tracks;
    this.totalPages = Math.max(1, Math.ceil(this.filteredTracks.length / TRACKS_PER_PAGE));
    this.currentPage = Math.min(Math.max(1, this.currentPage), this.totalPages);
    this.updateDisplayedTracks();
  }

  private filterTracks(tracks: LearningTrack[]): LearningTrack[] {
    return tracks.filter(track => {
      if (!this.matchesAvailabilityAndProgress(track)) {
        return false;
      }
      if (!this.matchesRecentContent(track)) {
        return false;
      }
      return true;
    });
  }

  private matchesAvailabilityAndProgress(track: LearningTrack): boolean {
    const { availability, progress } = this.filters;
    const status = track.progress_status;

    if (availability === 'all' && progress === 'all') {
      return true;
    }

    if (availability === 'available') {
      if (progress === 'all') {
        return true;
      }
      return status === progress;
    }

    // availability === 'all'
    return progress === 'all' || status === progress;
  }

  private matchesRecentContent(track: LearningTrack): boolean {
    const { recentContent } = this.filters;
    if (recentContent === 'all') {
      return true;
    }
    if (recentContent === 'recently_added') {
      return !!track.has_recently_added_content;
    }
    if (recentContent === 'recently_updated') {
      return !!track.has_recently_updated_content;
    }
    return true;
  }

  private sortTracks(tracks: LearningTrack[]): LearningTrack[] {
    if (this.filters.sort === 'default') {
      return tracks;
    }

    const sorted = [...tracks];
    sorted.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      const cmp = nameA.localeCompare(nameB);
      return this.filters.sort === 'name_asc' ? cmp : -cmp;
    });
    return sorted;
  }

  private updateDisplayedTracks(): void {
    const start = (this.currentPage - 1) * TRACKS_PER_PAGE;
    this.displayedTracks = this.filteredTracks.slice(start, start + TRACKS_PER_PAGE);
  }

  getCompletedCount(track: LearningTrack): number {
    return track.course_tracks?.filter(ct => ct.user_has_completed_course_track).length ?? 0;
  }
}
